import {
  type RefCallback,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

interface UseIsOverflowingProps {
  enabled?: boolean;
  checkIfOverflowing?: (node: HTMLDivElement) => boolean;
}

const defaultCheckIfOverflowing = (node: HTMLDivElement) => {
  if (!node) {
    return false;
  }

  return node.scrollWidth > node.clientWidth;
};

export const useIsOverflowing = ({
  enabled = true,
  checkIfOverflowing = defaultCheckIfOverflowing
}: UseIsOverflowingProps = {}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const refCallback: RefCallback<HTMLDivElement> = useCallback(
    (node) => {
      resizeObserverRef.current?.disconnect();
      ref.current = node;

      if (!node || !enabled) {
        return;
      }

      const changeIsOverflowing = () => {
        const newIsOverflowing = checkIfOverflowing(node);
        setIsOverflowing(newIsOverflowing);
      };

      resizeObserverRef.current = new ResizeObserver(changeIsOverflowing);
      resizeObserverRef.current.observe(node);

      changeIsOverflowing();
    },
    [checkIfOverflowing, enabled]
  );

  const revalidate = useCallback(() => {
    if (!ref.current) {
      return;
    }

    const newIsOverflowing = checkIfOverflowing(ref.current);
    setIsOverflowing(newIsOverflowing);
  }, [checkIfOverflowing]);

  useEffect(() => {
    if (!enabled) {
      resizeObserverRef.current?.disconnect();
      setIsOverflowing(false);
    }
  }, [enabled]);

  return { ref: refCallback, isOverflowing, revalidate };
};
